import { AccordionContent, AccordionItem, AccordionTrigger } from "~/components/ui/custom-radix-components/accordion";
import convertCurrency from "~/lib/helpers/currencyConverter";
import { useCurrentCurrency } from "~/lib/helpers/CurrencyHelper";
import CircularProgressChart from "../Charts/CircularProgressBar";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from "chart.js";

ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

export function DeskAccordionRow({ row, index, areaSummary }: { row: any; index: number; areaSummary: any }) {

  const { currency } = useCurrentCurrency()
  const barData = (() => {
    const barValues = [
      { label: "Enterprise Suite", value: row.enterpriseSpacePrice || 0 },
      { label: "Private Office", value: row.privateSpacePrice || 0 },
      { label: "Sublet Office", value: row.subletSpacesPrice || 0 },
      { label: "Managed Space", value: row.managedSpacesPrice || 0 },
      { label: "Coworking Desk", value: row.coworkingSpacesPrice || 0 },
      { label: "Shared Office", value: row.sharedSpacesPrice || 0 },
      { label: "Virtual Office", value: row.virtualOfficePrice || 0 },
      { label: "Part time Desk", value: 0 },
    ];

    const sorted = barValues.sort((a, b) => b.value - a.value);

    const baseColors = [
      "#7CC6E4", "#609DDB", "#8E91E3", "#A183DC", "#B07DCC", "#CC79C6", "#E679B8", "#EC6DA9"
    ];

    return {
      labels: sorted.map(item => item.label),
      datasets: [
        {
          label: "Avg Price",
          data: sorted.map(item => item.value),
          backgroundColor: baseColors,
          borderRadius: 6,
        },
      ],
    };
  })();


  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: function (context) {
          let tooltipEl = document.getElementById('chartjs-tooltip');

          // Create tooltip element if it doesn't exist
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.style.background = 'white';
            tooltipEl.style.border = '1px solid #e5e7eb';
            tooltipEl.style.borderRadius = '8px';
            tooltipEl.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.1)';
            tooltipEl.style.color = '#111827';
            tooltipEl.style.opacity = 1;
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.padding = '6px 8px';
            tooltipEl.style.transition = 'all .1s ease';
            tooltipEl.style.font = 'sans-serif';
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;

          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          if (tooltipModel.body) {
            const dataIndex = tooltipModel.dataPoints[0].dataIndex;
            const label = context.chart.data.labels?.[dataIndex];
            const value = context.chart.data.datasets[0].data[dataIndex];

            tooltipEl.innerHTML = `
              <div style="display: flex; flex-direction: column;">
                <span style="font-weight: 600; font-size:12px;">${label}</span>
                <span style="color: #6b7280; font-size:12px;">Avg Price: ${value}</span>
              </div>
            `;
          }

          const position = context.chart.canvas.getBoundingClientRect();
          tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
          tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
          tooltipEl.style.opacity = 1;
        }
      },
      datalabels: {
        anchor: 'start',
        align: 'end',
        rotation: -90,
        color: '#000',
        font: {
          family: 'Inter, ui-sans-serif, system-ui, sans-serif',
          size: 13,
          weight: '500',
        },
        formatter: (value: number, context: any) => {
          return barData.labels[context.dataIndex];
        },
        clamp: false,
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            family: 'Inter, ui-sans-serif, system-ui, sans-serif',
          },
          display: false,
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
      },
    },
  };

  const isEven = index % 2 === 1;

  return (
    <AccordionItem value={`item-${index}`}>
      <AccordionTrigger className={`w-full text-left text-lg text-gray-500 hover:text-black py-4 px-3  ${isEven ? "bg-gray-50" : ""}`} >
        <div className={`grid grid-cols-6 w-full text-sm`}>
          <div className="font-medium">{row.suburb}</div>

          {[...row.areaProfileSummary, ...Array(5 - row.areaProfileSummary.length).fill(null)]
            .slice(0, 5)
            .map((i: any, idx: number) => {
              const isEmpty = !i || i.deskMonthlyPrice <= 0;

              return (
                <div
                  key={i?.sfid ?? `empty-${idx}`}
                  className={isEmpty ? "pl-4 text-gray-400" : "text-left"}
                >
                  {isEmpty
                    ? "-"
                    : convertCurrency(
                      i.deskMonthlyPrice,
                      areaSummary?.currencyIsoCode,
                      currency
                    )}
                </div>
              );
            })}
        </div>
      </AccordionTrigger>
      <AccordionContent className="bg-white px-6 pb-6">
        <div className=" gap-4 mt-4 flex ">
          <div className="flex-1 border-r  pr-4">
            <div className="text-md font-normal text-gray-500">Average Desk Price</div>
            <div className="text-3xl font-bold text-cyan-500">{convertCurrency(row.deskMonthlyPrice, areaSummary?.currencyIsoCode, currency)}</div>
            <div className="flex justify-between text-sm mt-2 text-gray-700">
              {/* <div>
                <div className="text-gray-800o">LOWEST PRICE</div>
                <div className="text-3xl font-bold text-gray-400">${convertCurrency(row.minMonthlyPrice, areaSummary?.currencyIsoCode, currency)}</div>
              </div> */}
              <div>
                <div className="text-gray-800o">LOWEST PRICE</div>
                <div className="text-3xl font-bold text-gray-400">${row.minMonthlyPrice?.toLocaleString()}</div>
              </div>
              {/* <div>
                <div className="text-gray-800o">HIGHEST PRICE</div>
                <div className="text-3xl font-bold text-gray-400">${convertCurrency(row.maxMonthlyPrice, areaSummary?.currencyIsoCode, currency)}</div>
              </div> */}
              <div>
                <div className="text-gray-800o">HIGHEST PRICE</div>
                <div className="text-3xl font-bold text-gray-400">${row.maxMonthlyPrice?.toLocaleString()}</div>
              </div>
            </div>

          </div>
          <div className="grid grid-cols-2 gap-4  text-sm flex-[2]">
            <div className="flex items-start justify-between gap-2 border-b">
              <div className="font-medium text-gray-700">
                <p className="text-sm">{row.spaceTypeFormula}</p>
                <p className={'text-gray-400 text-xs'}>{row.spaceTypeFormula} Listing in {row.suburb}</p>
              </div>
              <div className="text-cyan-600 font-bold">{row.availableSpacesBySpacetype}</div>
            </div>
            <div className="flex items-start justify-between gap-2 border-b">
              <div className="font-medium text-gray-700">
                <p className="text-sm">A Grade Building</p>
                <p className={'text-gray-400 text-xs'}>Listing in A Grade Building</p>
              </div>
              <CircularProgressChart value={row.agradeBuildingPercentage || 0} />
            </div>
            <div className="flex items-start justify-between gap-2">
              <div className="font-medium text-gray-700">
                <p className="text-sm">Available Spaces</p>
                <p className={'text-gray-400 text-xs'}>All Available Listing in Sydney</p>
              </div>
              <div className="text-cyan-600 font-bold">{row.totalAvailableSpaces}</div>
            </div>
            <div className="flex items-start justify-between gap-2">
              <div className="font-medium text-gray-700">
                <p className="text-sm">Discounted Listing</p>
                <p className={'text-gray-400 text-xs'}>Listing does have discounted price</p>
              </div>
              <CircularProgressChart value={row?.discountedListingsPercentage || 0} />
            </div>
          </div>


        </div>

        <div className="mt-6 flex gap-4">
          <div>
            <div className="text-lg font-bold mb-2">Growth Summary</div>
            <div className="overflow-x-auto">
              <table className="min-w-full border text-sm text-left">
                <thead className="bg-gray-50 font-semibold">
                  <tr>
                    <th className="px-4 py-2">No. of Desks</th>
                    <th className="px-4 py-2">Average</th>
                    <th className="px-4 py-2">Growth</th>
                    <th className="px-4 py-2"># of Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {row.areaProfileSummary.map((d, i) => (
                    <tr key={i} className={i % 2 === 1 ? "bg-gray-50" : ""}>
                      <td className="px-4 py-2">{d.name}</td>
                      <td className="px-4 py-2">{convertCurrency(d.deskMonthlyPrice, areaSummary?.currencyIsoCode, currency)}</td>
                      <td className="px-4 py-2">{d.enquriesPercentage}</td>
                      <td className="px-4 py-2">{d.salesPercentage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex-1">
            <div className="text-lg font-bold mb-2 ">Average Desk Price</div>
            <Bar data={barData} options={barOptions} plugins={[ChartDataLabels]} />
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}